import Link from 'next/link'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast-v2'

import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { IndicatorCard } from 'app/KinerjaPage/pages/PelaksanaanV2/components/IndicatorCard'
import { tahapanPelaksanaan } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/tahapanPelaksanaan'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS } from 'app/KinerjaShared/constants/pelaksanaanSteps'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { REFERRER_KEY } from 'utils/hooks/useRouterHistory'
import { assetUrl } from 'utils/url'
import TahapanPelaksanaanItems from '../components/Beranda/TahapanPelaksanaanItem'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type {
  LangkahObservasi,
  TahapanPelaksanaanType,
} from 'app/KinerjaPage/pages/PelaksanaanV2/constants/tahapanPelaksanaan'

type PelaksanaanObservasiProps = {
  overridePeriodPayload?: boolean
  isViewOnly?: boolean
  period?: number
  year?: number
  resolveStepUrl?: (url: LangkahObservasi['url']) => string
}

const defaultStepUrlResolved = (url) => url

const PelaksanaanObservasi = ({
  overridePeriodPayload = false,
  isViewOnly = false,
  period,
  year,
  resolveStepUrl = defaultStepUrlResolved,
}: PelaksanaanObservasiProps) => {
  const router = useRouter()
  const toast = useToast()

  const periodParams = overridePeriodPayload
    ? { skp_period: period, skp_year: year }
    : undefined

  const { isSuccess, isLoading, data } = useAPIPelaksanaanProgress({
    periodParams,
  })
  const isFilledBySystem = data?.data?.filledBySystem === true
  const { trackEvent } = useSKPPelaksanaanTrackEvent(
    overridePeriodPayload ? { period, year } : undefined
  )
  const [, , , getReferrerValue] = useLocalStorage(REFERRER_KEY, '')
  const { periodNo, periodYear } = usePeriodContext()

  const periodContent = overridePeriodPayload
    ? { period, year }
    : { period: periodNo, year: periodYear }

  // Redirect to Guru Homepage if data is filled by system
  // Ref: https://wartek.atlassian.net/browse/GCPM-3939
  useLayoutEffect(() => {
    if (isFilledBySystem) {
      toast({
        message: 'Praktik Kinerja Anda telah diselesaikan secara sistem.',
      })
      router.replace('/pengelolaan-kinerja/guru')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilledBySystem])

  const PeriodHeaderSection = ({ isSuccess, isLoading }) => {
    if (isLoading)
      return (
        <div className="w-full flex justify-center">
          <Spinner />
        </div>
      )
    if (isSuccess)
      return (
        <div className="self-end flex bg-white rounded-lg p-4 justify-center items-center">
          <Text variant="subheading" className="mr-2 !text-[#65686C]">
            PERIODE
          </Text>
          <Text variant="heading-sm" className="!text-blue-60">
            <CardPeriod
              period={periodContent.period}
              year={periodContent.year}
              enableMultiplePeriodSupport
            />
          </Text>
        </div>
      )
  }

  const CURRENT_SCREEN_NAME = 'pelaksanaan_hub'

  useEffect(() => {
    const referrer = getReferrerValue()
    const event_trigger =
      referrer ===
      `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/dokumen-persiapan`
        ? 'form_a'
        : 'pelaksanaan_hub'

    trackEvent({
      activity_type: 'pelaksanaan_open_pelaksanaan_homepage',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isEvaluated = data?.data?.evaluationDocumentState === 'READY'

  const resolvedTahapanPelaksanaan: TahapanPelaksanaanType = useMemo(() => {
    const resolvedValue = Object.values(tahapanPelaksanaan).map(
      (listTahapanPelaksanaan) =>
        listTahapanPelaksanaan.map((item) => {
          const resolvedItem = {
            ...item,
            isViewOnly,
            url: resolveStepUrl(item.url),
          }

          // Disable the Edit button if the pelaksanaan is evaluated
          if (isEvaluated) {
            resolvedItem.editCtaText = null
          }

          // Override the period payload for tracker if needed
          if (overridePeriodPayload && resolvedItem.CtaButtonProps?.event) {
            resolvedItem.CtaButtonProps.event = {
              ...resolvedItem.CtaButtonProps?.event,
              attributes: {
                ...resolvedItem.CtaButtonProps?.event?.attributes,
                period,
                year,
              },
            }
          }

          return resolvedItem
        })
    )

    return {
      bagian1: resolvedValue[0],
      bagian2: resolvedValue[1],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEvaluated, isViewOnly, resolveStepUrl])

  // To avoid flicker, display loading spinner when fetching
  // and if the data is filled by the system (waiting for redirection)
  if (isLoading || isFilledBySystem)
    return (
      <div className="w-full flex justify-center pt-20">
        <Spinner />
      </div>
    )

  const progressPercentage = data.data.progressPercentage
  const handleRubrikDialogOpened = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_cek_rubrik_observasi',
      screen_name: CURRENT_SCREEN_NAME,
    })
  }

  const currentPelaksanaanStep =
    PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS[data?.data?.state]?.order ?? 0
  const minimalStepToComplete =
    PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS['REFLECTION_FOLLOW_UP_EVALUATION']
      ?.order ?? Infinity
  const isPelaksanaanGuruCompleted =
    currentPelaksanaanStep >= minimalStepToComplete

  return (
    <div className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex justify-between items-center">
          <Header
            title="Pelaksanaan Praktik Kinerja"
            className="mb-9"
            rightNode={
              <PeriodHeaderSection
                isSuccess={isSuccess}
                isLoading={isLoading}
              />
            }
          />
        </div>

        {isPelaksanaanGuruCompleted && <BannerCompletedPelaksanaan />}

        {/* content start from here */}
        <div className="bg-white rounded-lg">
          <div className="flex flex-col from-[#125096] to-[#74CBFB] bg-gradient-to-r rounded-t-lg ">
            <Text className="p-6" color="inverse" variant="heading-lg">
              Periode:{' '}
              <CardPeriod
                period={periodContent.period}
                year={periodContent.year}
                enableMultiplePeriodSupport
                withDate
              />
            </Text>
          </div>
          <div className="p-6">
            <div className="space-y-2">
              <div className="space-y-1">
                <Text variant="heading-sm">Praktik Kinerja</Text>
                <Text color="subdued">
                  Berikut rincian Pelaksanaan Praktik Kinerja.
                </Text>
              </div>

              <IndicatorCard
                subIndicatorName={data.data.subIndicatorName}
                subIndicatorDefinition={data.data.subIndicatorDefinition}
                qualityTarget={data.data.qualityTarget}
                contentId={data?.data.contentId}
                isLoading={isLoading}
                onRubrikDialogOpened={handleRubrikDialogOpened}
              />
              <TahapanPelaksanaanItems
                state={data.data.state}
                latestFormState={data?.data?.latestFormState}
                appealState={data?.data?.appealState}
                progressPercentage={progressPercentage}
                tahapanPelaksanaanConstant={resolvedTahapanPelaksanaan}
                periodParams={periodParams}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PelaksanaanObservasi

function BannerCompletedPelaksanaan() {
  return (
    <div className="mb-6 bg-white border-gray-30 border rounded">
      <div className="p-6 flex space-x-6 items-center">
        <img
          height={84}
          width={84}
          src={assetUrl('/illustrations/img-thumbs-up.png')}
          className="flex-shrink-0"
          alt=""
        />
        <div className="items-center space-y-2 flex-1">
          <Text variant="heading-md">Pelaksanaan Praktik Kinerja selesai!</Text>
          <Text variant="eyebrow-lg" color="subdued">
            Anda dapat lanjut cek dokumen Pengembangan Kompetensi dan Tugas
            Tambahan melalui Beranda.
          </Text>
        </div>

        <Link href={PENGELOLAAN_KINERJA_GURU_PATH} passHref>
          <Button color="blue" type={undefined} as="a" size="lg">
            Ke Beranda
          </Button>
        </Link>
      </div>
    </div>
  )
}
